import React, { useMemo, useCallback, useEffect, useRef } from "react";import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  ColumnSeries,
  Tooltip,
  Inject,
  AreaSeries,
  Zoom,
  Crosshair,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import {
  MINUTE_ZOOM_SETTINGS,
  NO_CROSSHAIR,
  NO_ZOOM_SETTINGS,
  minuteCrosshair,
  minuteLabel,
  useMinuteAxis,
} from "./minuteAxis";
import "./Chart.css";

/* ---------- helpers ---------- */

function parseHourFromLabel(label) {
  const m = String(label || "").match(/(\d{2})\s*:\s*\d{2}/);
  if (!m) return null;
  const hh = Number(m[1]);
  return Number.isFinite(hh) ? hh : null;
}

function inferDaysCountFromLabels(labels) {
  const days = new Set();
  for (const l of labels || []) {
    const m = String(l || "").match(/^(\d{2}[./-]\d{2})\b/);
    if (m?.[1]) days.add(m[1]);
  }
  return days.size || 1;
}

/* ---------- component ---------- */

export const AvgVideoChart = ({
  series = [],
  chartType = "line",
  title = "Average Video Consumption",
  loading = false,
  mode = "day",
  refreshKey,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);
  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";
  const tickColor = isDark ? "#64748b" : "#9ca3af";

  const {
    ref: wrapRef,
    range: minuteAxis,
    onZoomComplete: handleZoomComplete,
    axisProps: minuteAxisProps,
  } = useMinuteAxis({ series, mode, tickColor });

  const { chartData, yAxisMax, labels } = useMemo(() => {
    const clean = (Array.isArray(series) ? series : []).map((p, i) => ({
      x: minuteAxis ? p.index : i,
      y: Number(p?.value) || 0,
      label: String(p?.label ?? ""),
    }));

    const max = Math.max(...clean.map((d) => d.y), 0);

    return {
      chartData: clean,
      labels: clean.map((d) => d.label),
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
    };
  }, [series, minuteAxis]);

  /* =========================
     REAL AXIS STEP
  ========================= */
  const xInterval = mode === "minute" ? 60 : 1; // positional fallback

  /* =========================
     HOURLY STEP
  ========================= */
  const hourStep = useMemo(() => {
    if (mode !== "hour") return 1;
    const daysCount = inferDaysCountFromLabels(labels);
    return daysCount <= 7 ? 6 : 8;
  }, [mode, labels]);

  const showMarkers = mode === "day";

  /* =========================
     AXIS LABEL FILTER
  ========================= */
  const handleAxisLabelRender = useCallback(
    (args) => {
      // Format Y-axis numbers with thousand separators
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) {
          args.text = formatNumber(value, 2);
        }
        return;
      }

      if (args.axis?.name !== "primaryXAxis") return;

      const raw = Number(args.value);
      const idx = Math.round(raw);

      if (!Number.isFinite(raw) || Math.abs(raw - idx) > 1e-6) {
        args.text = "";
        return;
      }

      // Every tick the axis emits is already a clock minute.
      if (minuteAxis) {
        args.text = minuteLabel(idx);
        return;
      }

      const label = labels[idx];
      if (!label) {
        args.text = "";
        return;
      }

      if (mode === "hour") {
        const hh = parseHourFromLabel(label);
        if (!Number.isFinite(hh) || hh % hourStep !== 0) {
          args.text = "";
          return;
        }
      }

      if (mode === "minute") {
        if (!label.endsWith(":00")) {
          args.text = "";
          return;
        }
      }

      args.text = label;
    },
    [labels, mode, hourStep, minuteAxis]
  );

  /* =========================
     TOOLTIP
  ========================= */
  const handleTooltipRender = useCallback(
    (args) => {
      if (!args.point) return;
      const idx = Number.isFinite(args.point.index)
        ? args.point.index
        : Math.round(Number(args.point.x));
      const label = labels[idx] ?? "";
      args.text = `${label} : ${formatNumber(args.point.y, 2)} min`;
    },
    [labels]
  );

  const handleSharedTooltipRender = useCallback(
    (args) => {
      const point = args.point?.[0];
      if (!point) return;
      args.headerText = minuteLabel(
        chartData[point.index]?.x ?? Number(point.x)
      );
      args.text = [`${formatNumber(point.y, 2)} min`];
    },
    [chartData]
  );

  return (
    <div className="chart-container" ref={wrapRef}>
      <h3 className="chart-title">{title}</h3>

      {!loading && (
        <ChartComponent
          ref={chartRef}
          // Syncfusion throws if zoomSettings changes as a property, so the
          // axis kind remounts instead.
          key={minuteAxis ? "clock-axis" : "plain-axis"}
          width="100%"
          height="420px"
          primaryXAxis={{
            valueType: "Double",
            minimum: 0,
            maximum: Math.max(chartData.length - 1, 0),
            interval: xInterval,
            majorGridLines: { width: 0 },
            majorTickLines: { width: 0 },
            labelIntersectAction: "Rotate45",
            ...(minuteAxisProps || {}),
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          primaryYAxis={{
            name: "primaryYAxis",
            title: "Minutes",
            titleStyle: { color: axisTextColor },
            minimum: 0,
            maximum: yAxisMax,
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 },
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          tooltip={{ enable: true, shared: !!minuteAxis }}
          crosshair={minuteAxis ? minuteCrosshair(tickColor) : NO_CROSSHAIR}
          zoomSettings={minuteAxis ? MINUTE_ZOOM_SETTINGS : NO_ZOOM_SETTINGS}
          zoomComplete={handleZoomComplete}
          axisLabelRender={handleAxisLabelRender}
          tooltipRender={handleTooltipRender}
          sharedTooltipRender={handleSharedTooltipRender}
        >
          <Inject services={[LineSeries, ColumnSeries, AreaSeries, Tooltip, Zoom, Crosshair]} />

          <SeriesCollectionDirective>
            <SeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              type="Area"
              fill="#1976d2"
              opacity={0.12}
              border={{ width: 0 }}
              // Otherwise the fill adds a duplicate shared-tooltip row.
              enableTooltip={false}
            />

          <SeriesDirective
            dataSource={chartData}
            xName="x"
            yName="y"
            type={chartType === "bar" ? "Column" : "Line"}
            width={2}
            fill="#1976d2"
            border={{ color: "#1976d2", width: 2 }}
            marker={{
              visible: showMarkers,
              width: 7,
              height: 7,
              fill: "#fff",
              border: { width: 2, color: "#1976d2" },
            }}
            animation={{ enable: true, duration: 400 }}
          />
          </SeriesCollectionDirective>
        </ChartComponent>
      )}
    </div>
  );
};

export default AvgVideoChart;
